<template>
	<GlassCard class="blood-sugar-tracker p-3">
		<div class="d-flex align-items-center justify-content-between gap-2">
			<h2 class="mb-0">Blood Sugar</h2>
			<div class="d-flex align-items-center gap-2">
				<button
					v-if="Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android' && !userStore.isViewingUser()"
					@click="syncNow()"
					:disabled="isSyncing"
					class="btn btn-sm btn-outline-success"
				>
					<span v-if="isSyncing" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
					<i class="bi bi-arrow-repeat"></i>&nbsp;
					<i class="bi bi-heart-pulse-fill"></i>
				</button>
				<button @click="editTargetRange" class="btn btn-sm btn-outline-light" title="Target range" aria-label="Target range">
					<i class="bi bi-sliders"></i>
				</button>
				<button v-if="canEdit && isOnline" @click="openEditor()" class="btn btn-sm btn-primary"><i class="bi bi-plus-lg"></i> Add</button>
			</div>
		</div>

		<div class="btn-group w-100 mt-3" role="group" aria-label="View">
			<button type="button" class="btn btn-sm" :class="view === 'day' ? 'btn-light' : 'btn-outline-light'" @click="view = 'day'">
				Day
			</button>
			<button type="button" class="btn btn-sm" :class="view === 'trend' ? 'btn-light' : 'btn-outline-light'" @click="view = 'trend'">
				Trend
			</button>
		</div>

		<!-- Day navigation -->
		<div v-if="view === 'day'" class="day-nav mt-3">
			<button class="btn btn-outline-light btn-sm" @click="shiftDay(-1)" aria-label="Previous day">
				<i class="bi bi-chevron-left"></i>
			</button>
			<div class="day-label">
				<span>{{ dayLabel }}</span>
				<i class="bi bi-calendar3 ms-2"></i>
				<input
					type="date"
					class="day-input"
					:value="selectedDate"
					:max="today"
					aria-label="Pick a date"
					@click="$event.target.showPicker?.()"
					@change="onPickDate($event.target.value)"
				/>
			</div>
			<button class="btn btn-outline-light btn-sm" :disabled="isToday" @click="shiftDay(1)" aria-label="Next day">
				<i class="bi bi-chevron-right"></i>
			</button>
			<button v-if="!isToday" class="btn btn-outline-light btn-sm" @click="selectedDate = today">Today</button>
		</div>

		<!-- Trend range -->
		<div v-else class="btn-group w-100 mt-3" role="group" aria-label="Range">
			<button
				v-for="n in [7, 30, 90]"
				:key="n"
				type="button"
				class="btn btn-sm"
				:class="trendDays === n ? 'btn-light' : 'btn-outline-light'"
				@click="trendDays = n"
			>
				{{ n }} days
			</button>
		</div>

		<div class="stat-grid mt-3">
			<div v-for="s in stats" :key="s.label" class="stat">
				<div class="stat-value" :class="s.cls">{{ s.value }}</div>
				<div class="stat-label">{{ s.label }}</div>
			</div>
		</div>

		<div class="mt-3 chart-container" @touchstart.passive="onTouchStart" @touchend.passive="onTouchEnd">
			<canvas id="sugarChart" ref="sugarCanvas"></canvas>
			<div v-if="loading" class="chart-overlay">Loading…</div>
			<div v-else-if="isEmpty" class="chart-overlay">
				{{ view === 'day' ? 'No readings for this day.' : 'No readings in this period.' }}
			</div>
		</div>
		<div v-if="view === 'trend'" class="chart-caption">Line = daily average, shaded band = daily min–max. Tap a day to open it.</div>

		<!-- Day list -->
		<div v-if="view === 'day' && listRecords.length" class="reading-list mt-3">
			<div
				v-for="r in visibleRecords"
				:id="'bs-row-' + r.id"
				:key="r.id"
				class="reading-row"
				:class="{
					highlight: highlightId === r.id,
					clickable: canEdit
				}"
				@click="openEditor(r)"
			>
				<span class="dot" :class="statusOf(r.level)"></span>
				<span class="time">{{ fmtTime(r.timestamp) }}</span>
				<i v-if="r.from_health_connect" class="bi bi-heart-pulse-fill hc" title="Health Connect"></i>
				<span class="value ms-auto"> {{ r.level }} <small>mg/dL</small> </span>
				<i v-if="canEdit" class="bi bi-pencil ms-2 edit-hint"></i>
			</div>
		</div>

		<!-- Trend list -->
		<div v-if="view === 'trend' && trendList.length" class="reading-list mt-3">
			<div v-for="d in visibleTrend" :key="d.date" class="reading-row clickable" @click="goToDate(d.date)">
				<span class="dot" :class="statusOf(d.avg)"></span>
				<span class="time">{{ fmtDay(d.date) }}</span>
				<span class="text-muted-light small ms-2"> {{ d.min }}–{{ d.max }} · {{ d.count }}× </span>
				<span class="value ms-auto"> {{ d.avg }} <small>avg</small> </span>
				<i class="bi bi-chevron-right ms-2 edit-hint"></i>
			</div>
		</div>

		<button v-if="hasMore" type="button" class="btn btn-sm btn-outline-light w-100 mt-2" @click="listLimit += PAGE">
			Show more ({{ totalRows - listLimit }} more)
		</button>
	</GlassCard>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import GlassCard from './GlassCard.vue'
import Chart from 'chart.js/auto'
import Swal from 'sweetalert2'
import moment from 'moment-timezone'
import { useTokenStore } from '@/stores/token'
// import { hasTokenIssue, handleTokenIssue } from "@/utils/tokenIssue"
import { fetchWithAuth } from '@/utils/api-client'
import { useUserStore } from '@/stores/user'
import { isOnline } from '@/utils/networkBus'
import * as SubscriptionHelper from '@/utils/subscriptionHelper'
import { Capacitor } from '@capacitor/core'
import { Health } from '@/plugins/Health'
import { ensureTokenAndSyncStore } from '@/plugins/Health/sync'

const userStore = useUserStore()
const tokenStore = useTokenStore()
const { hasToken } = tokenStore

const DAY_FMT = 'YYYY-MM-DD'
const DEFAULT_TARGET = { low: 70, high: 180 }
const COLORS = {
	low: '#ef4444',
	in: '#2ecc71',
	high: '#fbbf24',
	line: 'rgba(75, 192, 192, 1)'
}

function loadTarget() {
	try {
		const t = JSON.parse(localStorage.getItem('bloodSugarTarget'))
		if (t && t.low > 0 && t.high > t.low) return { low: t.low, high: t.high }
	} catch {}
	return { ...DEFAULT_TARGET }
}

const target = ref(loadTarget())
const view = ref('day') // "day" | "trend"
const today = ref(moment().format(DAY_FMT))
const selectedDate = ref(today.value)
const trendDays = ref(30)
const dayCache = ref({}) // "YYYY-MM-DD" -> readings, oldest first
const trendData = ref([]) // per-day aggregates
const trendStale = ref(true)
let trendLoadedDays = 0
const loading = ref(false)
const highlightId = ref(null)
const sugarCanvas = ref(null)
let chart = null
let chartRecords = [] // readings behind the current day chart, by index
let highlightTimer = null

// Health Connect sync (native app only)
const isSyncing = ref(false)

const canEdit = computed(() => hasToken() && !userStore.isViewingUser())
const isToday = computed(() => selectedDate.value >= today.value)
const dayLabel = computed(() => moment(selectedDate.value, DAY_FMT).format('ddd, MMM D, YYYY'))
const dayRecords = computed(() => dayCache.value[selectedDate.value] || [])
const listRecords = computed(() => [...dayRecords.value].reverse())
const trendList = computed(() => [...trendData.value].reverse())

// only render a page of rows at a time; "Show more" reveals the rest
const PAGE = 10
const listLimit = ref(PAGE)
const visibleRecords = computed(() => listRecords.value.slice(0, listLimit.value))
const visibleTrend = computed(() => trendList.value.slice(0, listLimit.value))
const totalRows = computed(() => (view.value === 'day' ? listRecords.value.length : trendList.value.length))
const hasMore = computed(() => totalRows.value > listLimit.value)
const isEmpty = computed(() => (view.value === 'day' ? dayRecords.value.length === 0 : trendData.value.length === 0))

const fmtTime = (ts) => moment.unix(ts).format('h:mm A')
const fmtDay = (date) => moment(date, DAY_FMT).format('ddd, MMM D')

function statusOf(level) {
	if (level < target.value.low) return 'low'
	if (level > target.value.high) return 'high'
	return 'in'
}

const stats = computed(() => {
	let count = 0
	let avg = null
	let min = null
	let max = null
	let inRange = 0
	let extra = null
	if (view.value === 'day') {
		const rs = dayRecords.value
		count = rs.length
		if (count) {
			avg = Math.round(rs.reduce((s, r) => s + r.level, 0) / count)
			min = Math.min(...rs.map((r) => r.level))
			max = Math.max(...rs.map((r) => r.level))
			inRange = rs.filter((r) => statusOf(r.level) === 'in').length
		}
		extra = { label: 'Readings', value: count || '–' }
	} else {
		const ds = trendData.value
		count = ds.reduce((s, d) => s + d.count, 0)
		if (count) {
			avg = Math.round(ds.reduce((s, d) => s + d.avg * d.count, 0) / count)
			min = Math.min(...ds.map((d) => d.min))
			max = Math.max(...ds.map((d) => d.max))
			inRange = ds.reduce((s, d) => s + d.inRange, 0)
		}
		extra = { label: 'Days logged', value: ds.length || '–' }
	}
	return [
		{
			label: 'Average',
			value: avg ?? '–',
			cls: avg == null ? '' : statusOf(avg)
		},
		{ label: 'Min – Max', value: count ? `${min}–${max}` : '–' },
		{
			label: 'In range',
			value: count ? `${Math.round((inRange / count) * 100)}%` : '–'
		},
		extra
	]
})

// ---- data loading -------------------------------------------------------

async function apiGet(path) {
	const response = await fetchWithAuth(path, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
			'User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
		}
	})
	if (!response.ok) throw new Error('Network response was not ok')
	return response.json()
}

function dayBounds(date) {
	const m = moment(date, DAY_FMT)
	return [m.clone().startOf('day').unix(), m.clone().endOf('day').unix()]
}

async function loadDay(date) {
	if (dayCache.value[date]) return
	const [from, to] = dayBounds(date)
	dayCache.value[date] = await apiGet(`/api/bloodsugar-range?from=${from}&to=${to}`)
}

function prefetchNeighbors(date) {
	for (const offset of [-1, 1]) {
		const d = moment(date, DAY_FMT).add(offset, 'day').format(DAY_FMT)
		if (d > today.value) continue
		loadDay(d).catch(() => {})
	}
}

async function loadTrend() {
	if (!trendStale.value && trendLoadedDays === trendDays.value) return
	const from = moment()
		.startOf('day')
		.subtract(trendDays.value - 1, 'days')
		.unix()
	const to = moment().endOf('day').unix()
	const { low, high } = target.value
	const data = await apiGet(`/api/bloodsugar-range?from=${from}&to=${to}&bucket=day&low=${low}&high=${high}`)
	trendLoadedDays = trendDays.value
	trendData.value = data
	trendStale.value = false
}

let lastRefreshError = null
async function refresh() {
	lastRefreshError = null
	const v = view.value
	const date = selectedDate.value
	const days = trendDays.value
	const isCurrent = () => v === view.value && (v === 'day' ? date === selectedDate.value : days === trendDays.value)
	try {
		if (!dayCache.value[date] || v === 'trend') loading.value = true
		if (v === 'day') await loadDay(date)
		else await loadTrend()
	} catch (error) {
		lastRefreshError = error
		console.error('Failed to fetch blood sugar records:', error)
	} finally {
		if (isCurrent()) loading.value = false
	}
	if (!isCurrent()) return
	await nextTick()
	renderChart()
	if (v === 'day') prefetchNeighbors(date)
}

async function reloadAll() {
	dayCache.value = {}
	trendStale.value = true
	await refresh()
}

async function goToDate(date) {
	if (view.value === 'day' && selectedDate.value === date) await refresh()
	else {
		view.value = 'day'
		selectedDate.value = date
	}
}

watch([view, selectedDate, trendDays], () => {
	listLimit.value = PAGE
	refresh()
})

function shiftDay(delta) {
	const next = moment(selectedDate.value, DAY_FMT).add(delta, 'day').format(DAY_FMT)
	if (next <= today.value) selectedDate.value = next
}

function onPickDate(value) {
	if (!value) return
	selectedDate.value = value > today.value ? today.value : value
}

// swipe left/right on the chart to change day
let touchStart = null
function onTouchStart(e) {
	const t = e.changedTouches[0]
	touchStart = { x: t.clientX, y: t.clientY }
}
function onTouchEnd(e) {
	if (view.value !== 'day' || !touchStart) return
	const t = e.changedTouches[0]
	const dx = t.clientX - touchStart.x
	const dy = t.clientY - touchStart.y
	touchStart = null
	if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 2) {
		shiftDay(dx > 0 ? -1 : 1)
	}
}

// ---- chart --------------------------------------------------------------

const targetBandPlugin = {
	id: 'targetBand',
	beforeDatasetsDraw(c) {
		const { ctx, chartArea, scales } = c
		if (!chartArea || !scales.y) return
		const top = scales.y.getPixelForValue(Math.min(target.value.high, scales.y.max))
		const bottom = scales.y.getPixelForValue(Math.max(target.value.low, scales.y.min))
		if (bottom <= top) return
		ctx.save()
		ctx.fillStyle = 'rgba(46, 204, 113, 0.12)'
		ctx.fillRect(chartArea.left, top, chartArea.right - chartArea.left, bottom - top)
		ctx.restore()
	}
}

function baseOptions() {
	return {
		animation: false,
		responsive: true,
		maintainAspectRatio: false,
		interaction: { mode: 'nearest', intersect: true },
		plugins: { legend: { display: false } },
		scales: {
			y: {
				suggestedMin: Math.max(0, target.value.low - 20),
				suggestedMax: target.value.high + 20,
				ticks: { color: 'white' },
				grid: { color: 'rgba(255,255,255,0.1)' }
			}
		}
	}
}

function renderChart() {
	if (chart) {
		chart.destroy()
		chart = null
	}
	if (isEmpty.value) return
	const canvasEl = sugarCanvas.value || document.getElementById('sugarChart')
	const ctx = canvasEl && canvasEl.getContext ? canvasEl.getContext('2d') : null
	if (!ctx) {
		console.warn('BloodSugar chart: canvas 2D context not available')
		return
	}
	chart = view.value === 'day' ? buildDayChart(ctx) : buildTrendChart(ctx)
}

function buildDayChart(ctx) {
	const [dayStart] = dayBounds(selectedDate.value)
	chartRecords = dayRecords.value
	const n = chartRecords.length
	const opts = baseOptions()
	opts.scales.x = {
		type: 'linear',
		min: 0,
		max: 1440,
		ticks: {
			color: 'white',
			stepSize: 180,
			callback: (v) => moment().startOf('day').add(v, 'minutes').format('h A')
		},
		grid: { color: 'rgba(255,255,255,0.1)' }
	}
	opts.plugins.tooltip = {
		callbacks: {
			title: (items) => fmtTime(chartRecords[items[0].dataIndex].timestamp),
			label: (item) => `${item.parsed.y} mg/dL`
		}
	}
	opts.onClick = (_e, elements) => {
		if (!elements.length) return
		focusRow(chartRecords[elements[0].index].id)
	}
	const colors = chartRecords.map((r) => COLORS[statusOf(r.level)])
	return new Chart(ctx, {
		type: 'line',
		plugins: [targetBandPlugin],
		data: {
			datasets: [
				{
					data: chartRecords.map((r) => ({
						x: (r.timestamp - dayStart) / 60,
						y: r.level
					})),
					borderColor: COLORS.line,
					borderWidth: 1.5,
					tension: 0.3,
					pointRadius: n > 80 ? 1.5 : n > 30 ? 3 : 5,
					pointHoverRadius: 7,
					pointHitRadius: 12,
					pointBackgroundColor: colors,
					pointBorderColor: colors
				}
			]
		},
		options: opts
	})
}

function buildTrendChart(ctx) {
	const from = moment()
		.startOf('day')
		.subtract(trendDays.value - 1, 'days')
	const dates = []
	for (let i = 0; i < trendDays.value; i++) dates.push(from.clone().add(i, 'days').format(DAY_FMT))
	const byDate = new Map(trendData.value.map((d) => [d.date, d]))
	const pick = (key) => dates.map((d) => byDate.get(d)?.[key] ?? null)
	const opts = baseOptions()
	opts.scales.x = {
		ticks: {
			color: 'white',
			maxTicksLimit: 7,
			maxRotation: 0,
			callback(value) {
				return moment(dates[value], DAY_FMT).format('M/D')
			}
		},
		grid: { color: 'rgba(255,255,255,0.1)' }
	}
	opts.plugins.tooltip = {
		filter: (item) => item.datasetIndex === 2,
		callbacks: {
			title: (items) => fmtDay(dates[items[0].dataIndex]),
			label: (item) => {
				const d = byDate.get(dates[item.dataIndex])
				return d ? [`Average ${d.avg} mg/dL`, `Range ${d.min}–${d.max}`, `${d.count} reading${d.count === 1 ? '' : 's'}`] : ''
			}
		}
	}
	opts.onClick = (_e, elements) => {
		if (!elements.length) return
		const date = dates[elements[0].index]
		if (byDate.has(date)) goToDate(date)
	}
	const n = trendDays.value
	return new Chart(ctx, {
		type: 'line',
		plugins: [targetBandPlugin],
		data: {
			labels: dates.map((_, i) => i),
			datasets: [
				{
					data: pick('max'),
					borderWidth: 0,
					pointRadius: 0,
					pointHitRadius: 0,
					fill: '+1',
					backgroundColor: 'rgba(75, 192, 192, 0.2)'
				},
				{
					data: pick('min'),
					borderWidth: 0,
					pointRadius: 0,
					pointHitRadius: 0
				},
				{
					data: pick('avg'),
					borderColor: COLORS.line,
					borderWidth: 2,
					tension: 0.25,
					spanGaps: true,
					pointRadius: n > 30 ? 2 : 4,
					pointHoverRadius: 6,
					pointHitRadius: 12,
					pointBackgroundColor: dates.map((d) => {
						const avg = byDate.get(d)?.avg
						return avg == null ? COLORS.line : COLORS[statusOf(avg)]
					})
				}
			]
		},
		options: opts
	})
}

function focusRow(id) {
	const idx = listRecords.value.findIndex((r) => r.id === id)
	if (idx >= listLimit.value) listLimit.value = idx + 1
	highlightId.value = id
	clearTimeout(highlightTimer)
	highlightTimer = setTimeout(() => (highlightId.value = null), 2000)
	nextTick(() => document.getElementById(`bs-row-${id}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }))
}

// ---- target range -------------------------------------------------------

async function editTargetRange() {
	const { value } = await Swal.fire({
		title: 'Target range (mg/dL)',
		html:
			`<input id="bs-low" type="number" inputmode="numeric" class="swal2-input" placeholder="Low" value="${target.value.low}">` +
			`<input id="bs-high" type="number" inputmode="numeric" class="swal2-input" placeholder="High" value="${target.value.high}">`,
		showCancelButton: true,
		confirmButtonText: 'Save',
		focusConfirm: false,
		preConfirm: () => {
			const low = Number(document.getElementById('bs-low').value)
			const high = Number(document.getElementById('bs-high').value)
			if (!(low > 0) || !(high > low)) {
				Swal.showValidationMessage('Enter a low value below the high value.')
				return false
			}
			return { low, high }
		}
	})
	if (!value) return
	target.value = value
	try {
		localStorage.setItem('bloodSugarTarget', JSON.stringify(value))
	} catch {}
	trendStale.value = true
	await refresh()
}

// ---- add / edit / delete ------------------------------------------------

function defaultNewMoment() {
	if (selectedDate.value === today.value) return moment()
	return moment(selectedDate.value, DAY_FMT).hour(12).minute(0)
}

async function openEditor(record = null) {
	if (!canEdit.value) return
	if (!record && !isOnline.value) return
	const when = record ? moment.unix(record.timestamp) : defaultNewMoment()
	const result = await Swal.fire({
		title: record ? 'Edit reading' : 'Add reading',
		html:
			`<label class="swal2-input-label" for="bs-time">Date and time</label>` +
			`<input id="bs-time" type="datetime-local" class="swal2-input" value="${when.format('YYYY-MM-DDTHH:mm')}">` +
			`<label class="swal2-input-label" for="bs-level">Blood sugar (mg/dL)</label>` +
			`<input id="bs-level" type="number" inputmode="numeric" step="1" min="1" class="swal2-input" value="${record ? record.level : ''}">`,
		showCancelButton: true,
		showDenyButton: !!record,
		confirmButtonText: 'Save',
		denyButtonText: 'Delete',
		focusConfirm: false,
		didOpen: () => document.getElementById('bs-level')?.focus(),
		preConfirm: () => {
			const dateTime = document.getElementById('bs-time').value
			const level = Math.round(Number(document.getElementById('bs-level').value))
			if (!dateTime || !(level > 0)) {
				Swal.showValidationMessage('Please fill out the date and blood sugar level fields.')
				return false
			}
			return { ts: moment(dateTime).unix(), level }
		}
	})
	if (result.isDenied && record) {
		const confirm = await Swal.fire({
			title: 'Are you sure you want to delete this record?',
			showCancelButton: true,
			confirmButtonText: 'Delete'
		})
		if (confirm.isConfirmed) await deleteRecord(record)
	} else if (result.isConfirmed) {
		const { ts, level } = result.value
		if (record) await updateRecord(record, ts, level)
		else await addRecord(ts, level)
	}
}

function cacheRemove(record) {
	for (const date of Object.keys(dayCache.value)) {
		const list = dayCache.value[date]
		const i = list.findIndex((r) => r.id === record.id)
		if (i > -1) list.splice(i, 1)
	}
}

function cacheInsert(record) {
	const date = moment.unix(record.timestamp).format(DAY_FMT)
	const list = dayCache.value[date]
	if (!list) return
	list.push(record)
	list.sort((a, b) => a.timestamp - b.timestamp)
}

// Health Connect helpers (native app only)
async function canWriteHealthConnect() {
	if (!Capacitor.isNativePlatform()) return false
	const { available } = await Health.isAvailable()
	if (!available) return false
	const gp = await Health.getGrantedPermissions().catch(() => ({
		granted: []
	}))
	return Array.isArray(gp?.granted) ? gp.granted.some((s) => String(s).includes('WRITE_BLOOD_GLUCOSE')) : false
}

async function writeHealthConnect(iso, level) {
	const wr = await Health.writeBloodGlucose({
		valueMgPerDl: Number(level),
		time: iso
	})
	if (wr && wr.id) {
		await fetchWithAuth('/api/health-connect/blood-sugars/upsert', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				identifier: wr.id,
				time: iso,
				level: Number(level),
				source: 'android.healthconnect'
			})
		})
	}
}

async function deleteHealthConnect(id, iso) {
	const mapRes = await fetchWithAuth(`/api/health-connect/blood-sugars/map/${id}`, { method: 'GET' })
	if (!mapRes.ok) return null
	const map = await mapRes.json()
	if (!map || !map.identifier) return null
	return Health.deleteBloodGlucose({ id: map.identifier, time: iso })
}

const toast = (icon, title, timer = 1300) =>
	Swal.fire({
		toast: true,
		position: 'top',
		icon,
		title,
		showConfirmButton: false,
		timer
	})

async function addRecord(ts, level) {
	const feature = 'Add blood sugar'
	if (SubscriptionHelper.isFreeAccount()) {
		SubscriptionHelper.showLockedModal(feature)
		return
	}
	try {
		const date = moment.unix(ts).format(DAY_FMT)
		await loadDay(date)
		if (dayCache.value[date].some((r) => r.timestamp === ts)) {
			Swal.fire({
				icon: 'error',
				title: 'Oops...',
				text: 'A record for this time already exists.'
			})
			return
		}
		const response = await fetchWithAuth('/api/bloodsugars', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ timestamp: ts, level })
		})
		if (!(await SubscriptionHelper.requestCheckSubscription(response, feature))) return
		if (!response.ok) throw new Error('Could not add record')

		const data = await response.json()
		cacheInsert({ id: data.id, timestamp: ts, level, from_health_connect: false })
		trendStale.value = true

		// If native app, also write to Health Connect and map the identifier in backend
		try {
			if (await canWriteHealthConnect()) {
				await writeHealthConnect(moment.unix(ts).toISOString(), level)
			}
		} catch (e) {
			console.warn('Failed to write to Health Connect or map', e)
		}
		await goToDate(date)
	} catch (error) {
		alert(error.message)
		console.error('Failed to add record:', error)
	}
}

async function deleteRecord(record) {
	try {
		// If Android native, try deleting from Health Connect first using mapping
		if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
			const iso = moment.unix(record.timestamp).toISOString()
			try {
				if (await canWriteHealthConnect()) {
					const delRes = await deleteHealthConnect(record.id, iso)
					if (delRes && delRes.deleted === 0) toast('warning', 'Could not delete from Health Connect')
					else if (delRes) toast('success', 'Deleted from Health Connect', 900)
				}
			} catch (e) {
				console.warn('HC delete failed', e)
				toast('error', 'Health Connect delete failed')
			}
		}

		const response = await fetchWithAuth(`/api/bloodsugars/${record.id}`, {
			method: 'DELETE',
			headers: { 'Content-Type': 'application/json' }
		})
		if (!response.ok) throw new Error('Network response was not ok')
		cacheRemove(record)
		trendStale.value = true
		await refresh()
		toast('success', 'Deleted', 900)
	} catch (error) {
		console.error('Failed to delete the record:', error)
	}
}

async function updateRecord(record, ts, level) {
	try {
		const timeChanged = ts !== record.timestamp
		const newDate = moment.unix(ts).format(DAY_FMT)
		if (timeChanged) await loadDay(newDate)
		const response = await fetchWithAuth(`/api/bloodsugars/${record.id}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(timeChanged ? { level, timestamp: ts } : { level })
		})
		if (response.status === 409) {
			Swal.fire({
				icon: 'error',
				title: 'Oops...',
				text: 'A record for this time already exists.'
			})
			return
		}
		if (!response.ok) throw new Error('Network response was not ok')

		const oldIso = moment.unix(record.timestamp).toISOString()
		cacheRemove(record)
		cacheInsert({ ...record, timestamp: ts, level })
		trendStale.value = true

		// Propagate to Health Connect: delete old record at old time, then write updated value and map
		try {
			if (Capacitor.getPlatform() === 'android' && (await canWriteHealthConnect())) {
				try {
					await deleteHealthConnect(record.id, oldIso)
				} catch (e) {
					console.warn('HC delete before update failed (continuing)', e)
				}
				try {
					await writeHealthConnect(moment.unix(ts).toISOString(), level)
				} catch (e) {
					console.warn('HC write after update failed', e)
				}
			}
		} catch (e) {
			console.warn('Failed to propagate update to Health Connect', e)
		}
		await goToDate(newDate)
	} catch (error) {
		console.error('Failed to update the record:', error)
	}
}

onMounted(() => refresh())

onBeforeUnmount(() => {
	clearTimeout(highlightTimer)
	if (chart) chart.destroy()
})

const HEALTH_CONNECT_BATCH_SIZE = 50
const RECONCILE_DAYS = 30

async function upsertHealthConnectBloodRecord(record) {
	const valueMgPerDl = Number(record?.valueMgPerDl)
	if (!record?.id || !record?.time || !Number.isFinite(valueMgPerDl)) {
		throw new Error(`Invalid Health Connect blood glucose record: ${JSON.stringify(record)}`)
	}

	const response = await fetchWithAuth('/api/health-connect/blood-sugars/upsert', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			identifier: record.id,
			time: record.time,
			// Keep the legacy field for currently deployed API versions.
			level: Math.round(valueMgPerDl),
			valueMgPerDl: +valueMgPerDl.toFixed(2),
			source: 'android.healthconnect'
		})
	})
	if (!response.ok) {
		throw new Error(`Health Connect blood glucose upsert failed (${response.status}): ${await response.text()}`)
	}
}

// Upsert many Health Connect readings, a batch per request.
async function upsertHealthConnectBloodRecords(records) {
	const valid = []
	for (const r of records) {
		const valueMgPerDl = Number(r?.valueMgPerDl)
		if (!r?.id || !r?.time || !Number.isFinite(valueMgPerDl)) {
			throw new Error(`Invalid Health Connect blood glucose record: ${JSON.stringify(r)}`)
		}
		valid.push({
			identifier: r.id,
			time: r.time,
			level: Math.round(valueMgPerDl),
			valueMgPerDl: +valueMgPerDl.toFixed(2)
		})
	}
	for (let i = 0; i < valid.length; i += HEALTH_CONNECT_BATCH_SIZE) {
		const slice = valid.slice(i, i + HEALTH_CONNECT_BATCH_SIZE)
		const response = await fetchWithAuth('/api/health-connect/blood-sugars/upsert-batch', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ source: 'android.healthconnect', records: slice })
		})
		if (response.status === 404) {
			// Backend without the batch endpoint yet: fall back to one request per reading.
			for (const r of slice)
				await upsertHealthConnectBloodRecord({
					id: r.identifier,
					time: r.time,
					valueMgPerDl: r.valueMgPerDl
				})
			continue
		}
		if (!response.ok) {
			throw new Error(`Health Connect blood glucose batch upsert failed (${response.status}): ${await response.text()}`)
		}
	}
}

// Show which Health Connect sync step failed so the error is actionable.
let syncStage = 'start'
function syncErrorHtml(stage, e) {
	const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c])
	return (
		`<div style="text-align:left;font-size:.85rem;word-break:break-word">` +
		`<b>Step:</b> ${esc(stage)}<br>` +
		`<b>Error:</b> ${esc(e?.name || 'Error')}: ${esc(e?.message || e)}` +
		`</div>`
	)
}

async function syncNow() {
	if (isSyncing.value) return
	// Do not allow syncing when viewing another member's data
	if (userStore.isViewingUser()) return
	const feature = 'Add blood sugar'
	if (SubscriptionHelper.isFreeAccount()) {
		SubscriptionHelper.showLockedModal(feature)
		return
	}

	// Guard: Android native only
	if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return
	let hasReadBG = false
	let hasWriteBG = false
	try {
		isSyncing.value = true
		syncStage = 'check Health Connect availability and permissions'
		const { available } = await Health.isAvailable()
		if (!available) return
		const gp = await Health.getGrantedPermissions()
		hasWriteBG = Array.isArray(gp?.granted) ? gp.granted.some((s) => String(s).includes('WRITE_BLOOD_GLUCOSE')) : false
		hasReadBG = Array.isArray(gp?.granted) ? gp.granted.some((s) => String(s).includes('READ_BLOOD_GLUCOSE')) : false

		if (!hasReadBG) {
			Swal.fire({
				icon: 'warning',
				title: 'Health Connect read permission is required to sync.',
				showConfirmButton: true
			})
			return
		}
		Swal.fire({
			toast: true,
			position: 'top',
			icon: 'info',
			title: 'Syncing…',
			showConfirmButton: false,
			timer: 1000
		})
		syncStage = 'pull changes from Health Connect'
		await applyHealthConnectChangesForBlood()
		syncStage = 'reload data from server'
		await reloadAll()
		if (lastRefreshError) throw lastRefreshError
		// Only attempt to reconcile (write/delete to Health Connect) if WRITE is granted
		syncStage = 'write app entries to Health Connect'
		await reconcileHealthConnectWithBackend(hasWriteBG)
		const statusMsg = hasWriteBG ? `Synced Health Connect` : `Synced reads from Health Connect`
		Swal.fire({
			toast: true,
			position: 'top',
			icon: 'success',
			title: statusMsg,
			showConfirmButton: false,
			timer: 1500
		})
	} catch (e) {
		console.warn('Health sync failed', e)
		Swal.fire({
			icon: 'error',
			title: 'Sync failed',
			html: syncErrorHtml(syncStage, e)
		})
	} finally {
		isSyncing.value = false
	}
}

// Ensure Health Connect matches backend for recent blood sugar entries
async function reconcileHealthConnectWithBackend(hasWriteBG) {
	try {
		if (!hasWriteBG) {
			console.warn('Skipping HC reconcile: WRITE_BLOOD_GLUCOSE not granted')
			return
		}

		// Only the last few days: this just pushes app-entered readings to Health Connect.
		const cutoff = moment().subtract(RECONCILE_DAYS, 'days').unix()
		const startIso = moment.unix(cutoff).toISOString()
		const endIso = moment().toISOString()

		const recent = (await apiGet(`/api/bloodsugar-range?from=${cutoff}&to=${moment().unix()}`)).filter(
			(r) => typeof r.timestamp === 'number'
		)

		// Readings that came from Health Connect (e.g. a CGM) are already there. If every
		// recent reading is one of those there is nothing to push, so don't read Health
		// Connect at all (a CGM writes ~288 readings a day, which trips its read quota).
		if (!recent.some((r) => !r.from_health_connect)) return

		// Read Health Connect records in the same recent window
		const hc = await Health.readBloodGlucose({
			startTime: startIso,
			endTime: endIso
		})
		const hcMap = new Map()
		for (const r of hc.records || []) {
			const ts = moment(r.time).unix()
			hcMap.set(ts, { id: r.id, value: Number(r.valueMgPerDl) })
		}

		// For each backend record in the window, ensure HC has the same value at the same instant
		for (const rec of recent) {
			const ts = rec.timestamp
			const mg = Number(rec.level)
			const iso = moment.unix(ts).toISOString()
			const hcRec = hcMap.get(ts)

			// If missing in HC, or value differs beyond tolerance, update HC to match app
			const tolerance = 1 // mg/dL
			if (!hcRec) {
				const wr = await Health.writeBloodGlucose({
					valueMgPerDl: mg,
					time: iso
				})
				if (!wr?.id) throw new Error('Health Connect did not return an ID for the blood glucose write')
				const response = await fetchWithAuth('/api/health-connect/blood-sugars/upsert', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						identifier: wr.id,
						time: iso,
						level: +mg.toFixed(2),
						source: 'android.healthconnect'
					})
				})
				if (!response.ok) throw new Error(`Health Connect blood glucose mapping failed (${response.status}): ${await response.text()}`)
			} else if (typeof hcRec.value === 'number' && typeof mg === 'number' && Math.abs(hcRec.value - mg) > tolerance) {
				// Value differs — delete existing HC record, then write app value and map
				await Health.deleteBloodGlucose({ id: hcRec.id, time: iso })
				const wr = await Health.writeBloodGlucose({
					valueMgPerDl: mg,
					time: iso
				})
				if (!wr?.id) throw new Error('Health Connect did not return an ID for the blood glucose write')
				const response = await fetchWithAuth('/api/health-connect/blood-sugars/upsert', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						identifier: wr.id,
						time: iso,
						level: +mg.toFixed(2),
						source: 'android.healthconnect'
					})
				})
				if (!response.ok) throw new Error(`Health Connect blood glucose mapping failed (${response.status}): ${await response.text()}`)
			}
		}
	} catch (e) {
		console.warn('Reconcile HC with backend failed', e)
		throw e
	}
}

async function applyHealthConnectChangesForBlood() {
	const tokenKey = 'blood_glucose'
	const recordTypes = ['BLOOD_GLUCOSE']

	await ensureTokenAndSyncStore(tokenKey, recordTypes)

	const deleted = []
	const upserts = []
	let total = 0
	let lastNextToken
	while (true) {
		const res = await Health.getChanges({
			tokenKey,
			recordTypes,
			token: lastNextToken
		})
		console.log('blood changes', JSON.stringify(res))
		if (res.changes) {
			for (const ch of res.changes) {
				if (ch.changeType === 'delete' && ch.recordType === 'BLOOD_GLUCOSE') deleted.push(ch)
				else if (ch.changeType === 'upsert' && ch.recordType === 'BLOOD_GLUCOSE') upserts.push(ch)
			}
			total += res.changes.length
		}
		lastNextToken = res.nextToken || lastNextToken
		if (!res.hasMore) break
	}

	const errors = []
	// Apply deletions first so a deleted record cannot be recreated by a stale mapping.
	for (const ch of deleted) {
		try {
			const res = await fetchWithAuth('/api/health-connect/blood-sugars/delete', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					identifier: ch.recordId,
					source: 'android.healthconnect'
				})
			})
			if (!res.ok) {
				errors.push(`delete ${ch.recordId}: ${await res.text()}`)
			}
		} catch (e) {
			errors.push(`delete ${ch.recordId}: ${e?.message || e}`)
		}
	}

	// Apply changes from Health Connect, including records written by other apps.
	try {
		await upsertHealthConnectBloodRecords(upserts.map((ch) => ch.record || {}))
	} catch (e) {
		errors.push(`upsert: ${e?.message || e}`)
	}

	if (errors.length) {
		throw new Error(
			`Health Connect blood glucose sync failed for ${errors.length} record${
				errors.length === 1 ? '' : 's'
			}: ${errors.slice(0, 3).join(' | ')}`
		)
	}

	if (lastNextToken) {
		await Health.saveChangesToken({
			tokenKey,
			token: String(lastNextToken)
		})
	}
	return { total }
}
</script>

<style lang="less" scoped>
.blood-sugar-tracker {
	max-width: 600px;
	margin: auto;

	.day-nav {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		.day-label {
			position: relative;
			flex: 1;
			text-align: center;
			color: white;
			font-weight: 600;
			padding: 0.25rem 0;
			.day-input {
				position: absolute;
				inset: 0;
				width: 100%;
				height: 100%;
				opacity: 0;
				cursor: pointer;
			}
		}
	}

	.stat-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 0.5rem;
		.stat {
			background: rgba(255, 255, 255, 0.06);
			border-radius: 0.75rem;
			padding: 0.4rem 0.25rem;
			text-align: center;
			.stat-value {
				color: white;
				font-weight: 700;
				font-size: 1.05rem;
				&.low {
					color: #ef4444;
				}
				&.in {
					color: #2ecc71;
				}
				&.high {
					color: #fbbf24;
				}
			}
			.stat-label {
				color: rgba(255, 255, 255, 0.65);
				font-size: 0.7rem;
			}
		}
	}

	.chart-container {
		position: relative;
		width: 100%;
		height: 240px;
		canvas {
			display: block;
			width: 100% !important;
			height: 100% !important;
		}
		.chart-overlay {
			position: absolute;
			inset: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			color: rgba(255, 255, 255, 0.75);
			pointer-events: none;
		}
	}
	.chart-caption {
		color: rgba(255, 255, 255, 0.6);
		font-size: 0.75rem;
		text-align: center;
		margin-top: 0.25rem;
	}

	.reading-list {
		.reading-row {
			display: flex;
			align-items: center;
			gap: 0.6rem;
			padding: 0.7rem 0.5rem;
			min-height: 48px;
			color: white;
			border-bottom: 1px solid rgba(255, 255, 255, 0.1);
			border-radius: 0.5rem;
			&.clickable {
				cursor: pointer;
				&:hover {
					background: rgba(255, 255, 255, 0.08);
				}
			}
			&.highlight {
				background: rgba(75, 192, 192, 0.25);
			}
			.dot {
				width: 0.75rem;
				height: 0.75rem;
				border-radius: 50%;
				flex: none;
				&.low {
					background: #ef4444;
				}
				&.in {
					background: #2ecc71;
				}
				&.high {
					background: #fbbf24;
				}
			}
			.hc {
				color: #2ecc71;
				font-size: 0.8rem;
			}
			.value {
				font-weight: 700;
				small {
					font-weight: 400;
					opacity: 0.65;
				}
			}
			.edit-hint {
				opacity: 0.5;
			}
			.text-muted-light {
				color: rgba(255, 255, 255, 0.6);
			}
		}
	}
}
</style>
